import { updateColumns } from "@tiptap/extension-table";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeView, ViewMutationRecord } from "@tiptap/pm/view";

const SCROLL_STEP_RATIO = 0.8;
const STYLE_ELEMENT_ID = "table-scroll-indicator-styles";

// Exact caret vector from the Figma spec (node 14060:131556), 6x11 glyph inside a 16x16 frame
const CARET_PATH = `M5.85403 5.85403L0.854028 10.854C0.807573 10.9005 0.752423 10.9373 0.691726 10.9625C0.63103 10.9876 0.565975 11.0006 0.500278 11.0006C0.434581 11.0006 0.369526 10.9876 0.30883 10.9625C0.248133 10.9373 0.192983 10.9005 0.146528 10.854C0.100073 10.8076 0.0632225 10.7524 0.0380812 10.6917C0.0129398 10.631 0 10.566 0 10.5003C0 10.4346 0.0129398 10.3695 0.0380812 10.3088C0.0632225 10.2481 0.100073 10.193 0.146528 10.1465L4.7934 5.50028L0.146528 0.854028C0.0527074 0.760208 0 0.63296 0 0.500278C0 0.367596 0.0527074 0.240348 0.146528 0.146528C0.240348 0.0527077 0.367596 0 0.500278 0C0.63296 0 0.760208 0.0527077 0.854028 0.146528L5.85403 5.14653C5.90052 5.19296 5.9374 5.24811 5.96256 5.30881C5.98772 5.36951 6.00067 5.43457 6.00067 5.50028C6.00067 5.56599 5.98772 5.63105 5.96256 5.69175C5.9374 5.75245 5.90052 5.80759 5.85403 5.85403Z`;
const CHEVRON_RIGHT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="${CARET_PATH}" transform="translate(5.5 2.5)" fill="currentColor"/></svg>`;
const CHEVRON_LEFT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="${CARET_PATH}" transform="translate(10.5 2.5) scale(-1 1)" fill="currentColor"/></svg>`;

const INDICATOR_STYLES = `
  .table-scroll-container {
    overflow-x: auto;
    max-width: 100%;
  }
  .table-scroll-fade {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 60px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 5;
  }
  .table-scroll-fade--left {
    left: 0;
    background: linear-gradient(to right, #ffffff, rgba(255, 255, 255, 0));
  }
  .table-scroll-fade--right {
    right: 0;
    background: linear-gradient(to left, #ffffff, rgba(255, 255, 255, 0));
  }
  .table-scroll-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    will-change: top;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 9999px;
    background: #ffffff;
    border: 1px solid #ececee;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
    color: #3f3f46;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 6;
  }
  .table-scroll-btn--left {
    left: 6px;
  }
  .table-scroll-btn--right {
    right: 6px;
  }
  .table-scroll-btn svg {
    width: 16px;
    height: 16px;
  }
  .table-scroll-fade--visible {
    opacity: 1;
  }
  .table-scroll-btn--visible {
    opacity: 1;
    pointer-events: auto;
  }
`;

function ensureIndicatorStyles() {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = INDICATOR_STYLES;
  document.head.appendChild(style);
}

function createFade(side: "left" | "right"): HTMLDivElement {
  const fade = document.createElement("div");
  fade.className = `table-scroll-fade table-scroll-fade--${side}`;
  fade.contentEditable = "false";
  return fade;
}

function createScrollButton(side: "left" | "right", onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `table-scroll-btn table-scroll-btn--${side}`;
  button.contentEditable = "false";
  button.setAttribute("aria-label", side === "left" ? "Scroll table left" : "Scroll table right");
  button.innerHTML = side === "left" ? CHEVRON_LEFT_SVG : CHEVRON_RIGHT_SVG;
  button.addEventListener("mousedown", (e) => e.preventDefault());
  button.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return button;
}

export class ScrollableTableView implements NodeView {
  node: ProseMirrorNode;
  cellMinWidth: number;
  dom: HTMLDivElement;
  scrollContainer: HTMLDivElement;
  table: HTMLTableElement;
  colgroup: HTMLTableColElement;
  contentDOM: HTMLTableSectionElement;

  private fadeLeft: HTMLDivElement;
  private fadeRight: HTMLDivElement;
  private buttonLeft: HTMLButtonElement;
  private buttonRight: HTMLButtonElement;
  private resizeObserver: ResizeObserver | null = null;
  private positionFrame: number | null = null;

  constructor(node: ProseMirrorNode, cellMinWidth: number) {
    ensureIndicatorStyles();

    this.node = node;
    this.cellMinWidth = cellMinWidth;

    this.dom = document.createElement("div");
    this.dom.className = "tableWrapper";
    this.dom.style.position = "relative";

    this.scrollContainer = this.dom.appendChild(document.createElement("div"));
    this.scrollContainer.className = "table-scroll-container";

    this.table = this.scrollContainer.appendChild(document.createElement("table"));
    this.colgroup = this.table.appendChild(document.createElement("colgroup"));
    updateColumns(node, this.colgroup, this.table, cellMinWidth);
    this.contentDOM = this.table.appendChild(document.createElement("tbody"));

    this.fadeLeft = this.dom.appendChild(createFade("left"));
    this.fadeRight = this.dom.appendChild(createFade("right"));
    this.buttonLeft = this.dom.appendChild(createScrollButton("left", () => this.scrollByStep(-1)));
    this.buttonRight = this.dom.appendChild(createScrollButton("right", () => this.scrollByStep(1)));

    this.scrollContainer.addEventListener("scroll", this.updateIndicators, { passive: true });
    this.resizeObserver = new ResizeObserver(() => {
      this.updateIndicators();
      this.scheduleButtonReposition();
    });
    this.resizeObserver.observe(this.scrollContainer);
    this.resizeObserver.observe(this.table);

    window.addEventListener("scroll", this.scheduleButtonReposition, { passive: true, capture: true });
    window.addEventListener("resize", this.scheduleButtonReposition);
    this.scheduleButtonReposition();
  }

  // Keeps the arrows vertically centered on the visible slice of the table, so
  // they stay reachable when the table is taller than the viewport.
  private scheduleButtonReposition = () => {
    if (this.positionFrame !== null) return;
    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null;
      this.repositionButtons();
    });
  };

  private repositionButtons() {
    const rect = this.dom.getBoundingClientRect();
    if (rect.height === 0) return;

    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight);
    let center = rect.height / 2;
    if (visibleBottom > visibleTop) {
      center = (visibleTop + visibleBottom) / 2 - rect.top;
    }
    const halfButton = 16;
    center = Math.min(Math.max(center, halfButton + 2), rect.height - halfButton - 2);

    const top = `${center}px`;
    this.buttonLeft.style.top = top;
    this.buttonRight.style.top = top;
  }

  private updateIndicators = () => {
    const el = this.scrollContainer;
    const canScrollLeft = el.scrollLeft > 1;
    const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

    this.fadeLeft.classList.toggle("table-scroll-fade--visible", canScrollLeft);
    this.buttonLeft.classList.toggle("table-scroll-btn--visible", canScrollLeft);
    this.fadeRight.classList.toggle("table-scroll-fade--visible", canScrollRight);
    this.buttonRight.classList.toggle("table-scroll-btn--visible", canScrollRight);
  };

  private scrollByStep(direction: 1 | -1) {
    this.scrollContainer.scrollBy({
      left: direction * this.scrollContainer.clientWidth * SCROLL_STEP_RATIO,
      behavior: "smooth"
    });
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) {
      return false;
    }
    this.node = node;
    updateColumns(node, this.colgroup, this.table, this.cellMinWidth);
    this.updateIndicators();
    return true;
  }

  ignoreMutation(mutation: ViewMutationRecord): boolean {
    if (!this.table.contains(mutation.target)) {
      return true;
    }
    return mutation.type === "attributes" && (mutation.target === this.table || this.colgroup.contains(mutation.target));
  }

  destroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.scrollContainer.removeEventListener("scroll", this.updateIndicators);
    window.removeEventListener("scroll", this.scheduleButtonReposition, { capture: true });
    window.removeEventListener("resize", this.scheduleButtonReposition);
    if (this.positionFrame !== null) {
      window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }
  }
}
